import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { EventsOrganizer } from "./EventsOrganizer";
import { InvitationsOrganizer } from "./InvitationsOrganizer";
import { getToken } from "../api/utils";
import "./EventHubPage.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Enum for view control
const VIEW = {
  ORGANIZER: "organizer",
  GUEST: "guest",
};

export const EventHubPage = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState(VIEW.ORGANIZER);

  // 🧩 State variables
  const [events, setEvents] = useState([]);       // Events I organize
  const [invites, setInvites] = useState([]);     // Events I’m invited to
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizerError, setOrganizerError] = useState("");
  const [invitesError, setInvitesError] = useState("");

  // --- API: Fetch events I organize ---
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/events/events/?limit=100`, {
        headers: {
          Authorization: `Bearer ${getToken() || ""}`,
          Accept: "application/json",
        },
        cache: "no-store",
      });

      if (!res.ok) throw new Error(`Failed to fetch events (${res.status})`);
      const data = await res.json();

      setEvents(data || []);
      setOrganizerError("");
    } catch (err) {
      console.error("Error fetching events:", err);
      setOrganizerError("Unable to fetch events you organize.");
    }
  }, []);

  // --- API: Fetch events I’m invited to ---
  const fetchInvites = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/events/invites/?limit=100`, {
        headers: {
          Authorization: `Bearer ${getToken() || ""}`,
          Accept: "application/json",
        },
        cache: "no-store",
      });

      if (!res.ok) throw new Error(`Failed to fetch invites (${res.status})`);
      const data = await res.json();
      setInvites(data || []);
      setInvitesError("");
    } catch (err) {
      console.error("Error fetching invites:", err);
      setInvitesError("Unable to fetch your invitations.");
    }
  }, []);

  // --- Fetch both lists on mount ---
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError(null);
      setOrganizerError("");
      setInvitesError("");
      await Promise.all([fetchEvents(), fetchInvites()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchEvents, fetchInvites]);

  useEffect(() => {
    const refreshOnFocus = () => {
      fetchEvents();
      fetchInvites();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshOnFocus();
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchEvents, fetchInvites]);

  // --- Navigation helpers ---
  const handleGlobalGuestManager = () => navigate("/global-guest-manager");
  const handleCreateEvent = () => navigate("/event-manager");

  // --- UI States ---
  if (loading) {
    return (
      <div className="event-hub-container">
        <h2>Loading your events...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-hub-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="event-hub-container">
      {/* <h1>Your Event Hub</h1> */}

      <div className="hub-header">
        {/* View Switcher Tabs */}
        <div className="hub-tabs">
          <button
            className={`hub-tab-button ${
              activeView === VIEW.ORGANIZER ? "active" : ""
            }`}
            onClick={() => setActiveView(VIEW.ORGANIZER)}
          >
            Events I Organize
          </button>
          <button
            className={`hub-tab-button ${
              activeView === VIEW.GUEST ? "active" : ""
            }`}
            onClick={() => setActiveView(VIEW.GUEST)}
          >
            Events I'm Invited To
          </button>
        </div>

        {/* Global Actions */}
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button className="global-guest-button" onClick={handleCreateEvent}>
            + Create New Event
          </button>
          <button
            className="global-guest-button"
            onClick={handleGlobalGuestManager}
          >
            Manage All Guests
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="hub-content">
        {activeView === VIEW.ORGANIZER && organizerError ? (
          <p className="rounded-2xl border border-[#f5c6d7] bg-[#fff4f8] px-4 py-3 text-sm text-[#9d295d]">
            {organizerError}
          </p>
        ) : null}
        {activeView === VIEW.GUEST && invitesError ? (
          <p className="rounded-2xl border border-[#f5c6d7] bg-[#fff4f8] px-4 py-3 text-sm text-[#9d295d]">
            {invitesError}
          </p>
        ) : null}
        {activeView === VIEW.ORGANIZER ? (            
          <EventsOrganizer events={events} onRefresh={fetchEvents} />
        ) : (
          <InvitationsOrganizer invites={invites} onRefresh={fetchInvites} />
        )}
      </div>
    </div>
  );
};
